import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [form, setForm]         = useState({ email: '', password: '' });
  const [error, setError]       = useState(
    searchParams.get('error') === 'google_denied'
      ? 'Google sign-in was cancelled.'
      : searchParams.get('error') === 'membership_inactive'
        ? 'Your 365reset membership is no longer active. Please contact us to regain access.'
        : ''
  );
  const [loading, setLoading]   = useState(false);
  const location = useLocation(); // where ProtectedRoute sent them from (e.g. a Delve Deeper link)
  const [showMagic, setShowMagic]   = useState(false);
  const [magicEmail, setMagicEmail] = useState('');
  const [magicSent, setMagicSent]   = useState(false);
  const [magicLoading, setMagicLoading] = useState(false);
  const [magicError, setMagicError]     = useState('');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate(location.state?.from?.pathname || '/dashboard');
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'no_password') {
        setShowMagic(true);
        setMagicEmail(form.email);
        setError('');
      } else {
        setError(err.response?.data?.error || 'Sign-in failed. Please check your details and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMagicRequest = async (e) => {
    e.preventDefault();
    setMagicError('');
    setMagicLoading(true);
    try {
      // Oct 29: remember where they were headed (e.g. a /live/ session link) for when the emailed link opens.
      try { if (location.state?.from?.pathname) localStorage.setItem('after_sign_in', JSON.stringify({ path: location.state.from.pathname, at: Date.now() })); } catch { /* fine without it */ }
      await api.post('/auth/magic-link', { email: magicEmail });
      setMagicSent(true);
    } catch (err) {
      setMagicError(err.response?.data?.error || 'Could not send link. Please try again.');
    } finally {
      setMagicLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-reset-cream px-4">
      <div className="w-full max-w-md">

        {/* Header */}
        <div className="mb-8">
          <h1 className="font-serif text-2xl font-bold text-reset-brown">365reset Dashboard</h1>
          <p className="text-reset-terracotta text-sm mt-1">
            Sign in with your 365reset membership email and password
          </p>
        </div>

        {/* Main sign-in form */}
        {!showMagic && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-reset-brown mb-1">Email</label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                required
                autoComplete="email"
                placeholder="Your 365reset membership email"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-reset-brown mb-1">Password</label>
              <input
                type="password"
                name="password"
                value={form.password}
                onChange={handleChange}
                required
                autoComplete="current-password"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck="false"
                placeholder="Your 365reset membership password"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
              />
            </div>

            {error && <p className="text-red-600 text-sm">{error}</p>}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-reset-terracotta text-white py-2.5 rounded-lg font-medium text-sm hover:opacity-90 disabled:opacity-50 transition"
            >
              {loading ? 'Accessing…' : 'Access my Dashboard'}
            </button>

            <div className="flex items-center gap-3 my-1">
              <div className="flex-1 border-t border-gray-200" />
              <span className="text-xs text-gray-400">or</span>
              <div className="flex-1 border-t border-gray-200" />
            </div>

            <button
              type="button"
              onClick={() => { setShowMagic(true); setMagicEmail(form.email); }}
              className="w-full border border-gray-300 rounded-lg py-2 text-sm text-reset-brown hover:bg-gray-50 transition"
            >
              Send me a sign-in link instead
            </button>
          </form>
        )}

        {/* Magic link form */}
        {showMagic && !magicSent && (
          <div>
            {form.email && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                No password found for this account — use a sign-in link to get started, then set your password in Settings.
              </div>
            )}
            <form onSubmit={handleMagicRequest} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-reset-brown mb-1">Your membership email</label>
                <input
                  type="email"
                  value={magicEmail}
                  onChange={e => setMagicEmail(e.target.value)}
                  required
                  autoComplete="email"
                  placeholder="Your 365reset membership email"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
                />
              </div>

              {magicError && <p className="text-red-600 text-sm">{magicError}</p>}

              <button
                type="submit"
                disabled={magicLoading}
                className="w-full bg-reset-terracotta text-white py-2.5 rounded-lg font-medium text-sm hover:opacity-90 disabled:opacity-50 transition"
              >
                {magicLoading ? 'Sending…' : 'Send sign-in link'}
              </button>
            </form>

            <button
              onClick={() => setShowMagic(false)}
              className="mt-3 text-sm text-gray-400 hover:text-gray-600 transition w-full text-center"
            >
              ← Back to password sign-in
            </button>
          </div>
        )}

        {/* Magic link sent confirmation */}
        {magicSent && (
          <div className="text-center space-y-3">
            <div className="text-4xl">📬</div>
            <h2 className="font-semibold text-reset-brown">Check your inbox</h2>
            <p className="text-sm text-gray-600">
              We sent a sign-in link to <strong>{magicEmail}</strong>.
              The link expires in 15 minutes.
            </p>
            <button
              onClick={() => { setMagicSent(false); setMagicEmail(''); setShowMagic(false); }}
              className="text-sm text-reset-terracotta hover:underline"
            >
              Use a different email
            </button>
          </div>
        )}

        {/* Footer links */}
        {!magicSent && (
          <div className="mt-6 space-y-2 text-center text-sm text-gray-500">
            <p>
              <Link to="/auth/forgot-password" className="text-reset-terracotta hover:underline">
                Forgot your password?
              </Link>
            </p>
            <p>
              First time accessing?{' '}
              <Link to="/auth/signup" className="text-reset-terracotta hover:underline">
                Press here
              </Link>
            </p>
            {/* Jenn, 2026-09-27: someone who opened a forwarded "Delve Deeper"
                link lands here — give them the way in. */}
            <p className="pt-2 border-t border-gray-100">
              Not a member?{' '}
              <Link to="/join" className="font-semibold text-reset-terracotta hover:underline">
                Join now
              </Link>
            </p>
          </div>
        )}

        {/* Oct 7 2026: Google's calendar-app review needs these reachable
            from the sign-in page (Settings → Privacy already has them). */}
        <p className="mt-6 text-center text-xs text-gray-500">
          <a href="https://365reset.com/privacy.html" target="_blank" rel="noopener noreferrer" className="hover:underline">Privacy Policy</a>
          {' · '}
          <a href="https://365reset.com/terms.html" target="_blank" rel="noopener noreferrer" className="hover:underline">Terms of Service</a>
        </p>
      </div>
    </div>
  );
}
